<template>
  <div class="treeview">
    <div class="position-relative mb-3">
      <input
        v-model="searchWord"
        class="form-control treeview__search-input"
        type="text"
        ref="input"
        :placeholder="this.$t('actions.search')"
        :disabled="loadingSearch || initialising"
        @input="search"/>
      <loading
        v-if="loadingSearch"
        class="position-absolute treeview__search-loading"
      />
    </div>
    <v-tree
      v-if="!initialising"
      class="tree__tree-view"
      ref="tree"
      :data="treeList"
      :tpl="template"
      @node-click="selectNode"
    />
    <s-loader
      v-if="initialising"
      class="py-5"
      size="md"
    />
    <s-empty-list
      v-if="isTreeEmpty()"
    />
  </div>
</template>

<script>
import VTree from 'vue-tree-halower';
import { latinize } from '@/services/stringHelper';
import Loading from '@/components/Loading';

const SEARCH_DELAY = 1000;

export default {
  name: 'AssetsTreeview',
  components: {
    VTree,
    Loading,
  },
  props: {
    tree: {
      type: Array,
      default: () => ([]),
    },
    selectedId: {
      type: String || Number,
      required: false,
    },
  },
  data: () => ({
    searchWord: '',
    loadingSearch: false,
    initialising: true,
  }),
  computed: {
    searchType() {
      return this.searchWord
        ? this.latinizedSearch
        : this.searchWord;
    },
    treeList() {
      if (this.selectedId) {
        this.expandSelectedIdTree(this.tree);
      }

      return this.tree;
    },
  },
  mounted() {
    this.waitTreeBeMounted();
  },
  methods: {
    selectNode(node) {
      this.$nextTick(() => {
        this.$set(node, 'expanded', !node.expanded);
        this.$set(node, 'hasExpanded', true);
        this.$emit('onClickNode', node);
      });
    },
    latinizedSearch(node) {
      return new RegExp(latinize(this.searchWord), 'i').test(latinize(node.title));
    },
    loadingAsPromise() {
      this.loadingSearch = true;
      return new Promise((done) => {
        setTimeout(() => {
          done();
        }, 100);
      });
    },
    search() {
      clearTimeout(this.searchListener);

      this.searchListener = setTimeout(() => {
        this.loadingAsPromise()
          .then(() => {
            this.collapseAllNodes(this.tree);
            this.$refs.tree.searchNodes();
            this.$refs.tree.searchNodes(this.searchType);
            this.$nextTick(() => this.$refs.input.focus());
            this.loadingSearch = false;
          });
      }, SEARCH_DELAY);
    },
    isTreeEmpty() {
      return !(this.treeList || []).some(({ visible }) => visible);
    },
    template(node) {
      const {
        title,
        displayTitle,
        tooltip,
        selected,
        searched,
      } = node;
      let titleClass = selected ? 'node-title node-selected' : 'node-title';
      if (searched) titleClass += ' node-searched';

      return <div class={titleClass} onClick={() => {
        this.$refs.tree.nodeSelected(node);
      }} v-b-tooltip={tooltip}>
        <div>{displayTitle || title}</div>
      </div>;
    },
    collapseAllNodes(list) {
      for (let i = 0; i < list.length; i += 1) {
        // eslint-disable-next-line no-param-reassign
        list[i].expanded = false;
        this.collapseAllNodes(list[i].children || []);
      }
    },
    expandSelectedIdTree(list) {
      for (let i = 0; i < list.length; i += 1) {
        if (list[i].id === this.selectedId) {
          // eslint-disable-next-line no-param-reassign
          return true;
        }

        const found = this.expandSelectedIdTree(list[i].children || []);
        // eslint-disable-next-line no-param-reassign
        list[i].expanded = found;

        if (found) return found;
      }
      return false;
    },
    waitTreeBeMounted() {
      setTimeout(() => {
        this.initialising = false;
      }, 600);
    },
  },
};
</script>

<style src="vue-tree-halower/dist/halower-tree.min.css"></style>
<style lang="sass" scoped>
::v-deep
  .halo-tree
    font-size: inherit

    &.tree__tree-view
      padding-left: 5px

      & > li
        margin: 0
        padding: 5px 5px 5px 5px
        position: relative
        list-style: none

      & > li:after,
      & > li:before
        content: none
        left: auto
        position: inherit
        right: auto
        border-width: 0

      & > li:before
        border-left: 0
        bottom: 50px
        height: 100%
        top: auto
        width: auto

      & > li:after
        border-top: none
        height: 20px
        top: 0
        width: auto

    & .tree-node-el
      position: relative
      padding-left: 25px
      top: -5px
      left: -10px
      background-color: transparent

    & .node-title
      display: inline-block
      padding: 3px
      margin: 0

      &:hover
        background-color: rgba(0, 0, 0, 0.1)

    & ul
      padding-top: 0
      padding-left: 10px

    & > li
      padding: 10px 5px 5px 10px

    & > li:after
      width: 15px

    & .tree-expand,
    & .tree-expand:hover
      border: none
      background-color: var(--color-page-filter-background)

    & > li:before
      border-left: 1px solid #999

    & > li:after
      border-top: 1px solid #999

    & li:last-child:before
      height: 25px

    & .tree-close:after
      //noinspection CssNoGenericFontName
      font-family: "Material Icons"
      content: "\e5cf"

    & .tree-open:after
      //noinspection CssNoGenericFontName
      font-family: "Material Icons"
      content: "\e5ce"

    & .node-selected
      border: none
      background-color: transparent

      &:hover
        background-color: rgba(0, 0, 0, 0.1)

    & .tree-open
      line-height: 24px

    & .tree-expand
      width: 20px
      height: 20px
      line-height: 20px
      position: absolute
      top: 3px
      left: 3px

    & li
      & .node-title.node-searched
        border: 1px solid var(--color-link)

      &.leaf:after
        border-top: 1px solid #999
        top: 16px
        width: 10px

      &.leaf
        & .tree-node-el
          padding-left: 10px

        & .node-title
          color: var(--color-link)

        & .node-title:hover
          text-decoration: underline

.treeview
  &__search-input
    padding-right: 1.8rem

  &__search-loading
    position: absolute
    top: 1rem
    right: .8rem

</style>

